'use client';

import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const glassPanelSx = {
  bgcolor: 'rgba(15,15,20,0.82)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.08)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
} as const;

const btnSx = {
  fontSize: '0.82rem',
  whiteSpace: 'nowrap',
  minWidth: 0,
  py: 0.35,
  px: 1.25,
  color: 'background.paper',
  borderColor: 'rgba(255,255,255,0.2)',
  '&:hover': {
    borderColor: 'rgba(255,255,255,0.5)',
    bgcolor: 'rgba(255,255,255,0.08)',
  },
  '&.Mui-disabled': {
    borderColor: 'rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.25)',
  },
} as const;

type InfoPanelProps = {
  open: boolean;
  onToggle: () => void;
};

export default function InfoPanel({ open, onToggle }: InfoPanelProps) {
  return (
    <>
      {/* Info panel — top-left */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 30,
          maxWidth: 350,
          px: 2.5,
          py: 2,
          borderRadius: 1,
          ...glassPanelSx,
          opacity: open ? 1 : 0,
          visibility: open ? 'visible' : 'hidden',
          transition: 'opacity 0.25s ease, visibility 0.25s ease',
        }}
      >
        <Typography
          variant='h6'
          sx={{ color: 'background.paper', fontWeight: 600, mb: 2 }}
        >
          Coronary Bypass Graft
        </Typography>
        <Typography
          variant='body2'
          sx={{ color: 'background.default', lineHeight: 1.6 }}
        >
          This application simulates blood flow dynamics via Reduced Order
          Models (ROMs) within a patient-specific coronary bypass following an
          isolated 70% stenosis of the Left Main Coronary Artery (LMCA) grafted
          with the Left Internal Thoracic Artery (LITA). The interactive
          interface enables fast exploration of the bypass under varying
          physiological conditions, such as changes in blood inflow within the
          LMCA or LITA over the cardiac cycle. Key hemodynamic variables,
          including velocity, pressure and Wall Shear Stress (WSS), can be
          visualized to analyze how different physiological states impact
          localized blood flow.
        </Typography>
        <Typography
          variant='caption'
          sx={{
            color: 'rgba(255,255,255,0.5)',
            display: 'block',
            mt: 2,
            lineHeight: 1.5,
            pl: 2,
            textIndent: '-1rem',
          }}
        >
          [1] Siena, P., Girfoglio, M., Ballarin, F. et al. Data-Driven Reduced
          Order Modelling for Patient-Specific Hemodynamics of Coronary Artery
          Bypass Grafts with Physical and Geometrical Parameters. J Sci Comput
          94, 38 (2023).{' '}
          <a
            href='https://doi.org/10.1007/s10915-022-02082-5'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'underline' }}
          >
            https://doi.org/10.1007/s10915-022-02082-5
          </a>
        </Typography>
        <Typography
          variant='caption'
          sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mt: 1, lineHeight: 1.5 }}
        >
          Geometry provided by Ospedale Sacco, Milan.
        </Typography>
      </Box>

      {/* Controls info — top-right */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 30,
          padding: '10px 14px',
          borderRadius: 1,
          ...glassPanelSx,
          color: 'background.default',
          fontSize: 13,
          pointerEvents: 'none',
          opacity: open ? 1 : 0,
          visibility: open ? 'visible' : 'hidden',
          transition: 'opacity 0.25s ease, visibility 0.25s ease',
        }}
      >
        <Box
          sx={{
            fontSize: 15,
            fontWeight: 500,
            color: 'background.paper',
            mb: 0.75,
          }}
        >
          Controls Info
        </Box>
        - Hold left mouse button and drag to rotate. <br />
        - Hold right mouse button and drag to pan. <br />
        - Scroll to zoom. <br />
        - Right-click the model to get detailed values. <br />- Press Enter to
        start a simulation.
      </Box>

      {/* Info toggler — bottom-left */}
      <Box sx={{ position: 'absolute', bottom: 16, left: 16, zIndex: 30 }}>
        <Button
          variant='outlined'
          size='small'
          onClick={onToggle}
          sx={{
            ...btnSx,
            borderRadius: 1,
            ...glassPanelSx,
            textTransform: 'none',
            '&:hover': {},
          }}
          startIcon={<InfoOutlinedIcon sx={{ fontSize: 12 }} />}
        >
          {open ? 'Hide info' : 'Show info'}
        </Button>
      </Box>
    </>
  );
}
